import { useEffect, useRef, useState } from 'react';
import { InvoiceStatus, PackageNameEnum } from '@electricjs/core_entity-client';
import { useTranslation } from 'react-i18next';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { ELIGIBLE_LICENSE_PLAN_NAMES } from '@/constants/packages';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetOrganizationPackagesQuery } from '@/redux/slices/packageApiSlice';
import {
  useCheckCustomerHasPendingSubscriptionChangeQuery,
  useGetOrganizationInvoicesV2Query,
  useAmendLicenseCountMutation,
} from '@/redux/slices/billingApiSlice';

const PAST_DUE_INVOICE_THRESHOLD = 2;

export const useAddLicensesModalState = (
  onClose: () => void,
  onSuccess?: () => void
) => {
  const { t } = useTranslation('people');
  const { showSuccessToast } = useGlobalUI();
  const organizationId = useGetOrganizationId();

  const {
    data: orgPackages = [],
    isLoading: isLoadingPackages,
    refetch: refetchOrganizationPackages,
  } = useGetOrganizationPackagesQuery(organizationId);
  const {
    data: hasPendingSubscriptionChange = false,
    isLoading: isLoadingPendingSubscriptionChange,
  } = useCheckCustomerHasPendingSubscriptionChangeQuery(organizationId, {
    refetchOnMountOrArgChange: true,
  });
  const {
    data: invoicesData,
    isLoading: isLoadingInvoices,
    refetch: refetchInvoices,
  } = useGetOrganizationInvoicesV2Query({ organizationId });

  // Admins pay overdue invoices in a separate tab, so refetch when they come back
  // to this one instead of leaving them stuck behind a stale banner.
  useEffect(() => {
    window.addEventListener('focus', refetchInvoices);
    return () => window.removeEventListener('focus', refetchInvoices);
  }, [refetchInvoices]);

  const pastDueInvoiceCount = (invoicesData?.invoices ?? []).filter(
    invoice => invoice.status === InvoiceStatus.Open && invoice.isOverdue
  ).length;

  const hasTooManyPastDueInvoices =
    pastDueInvoiceCount >= PAST_DUE_INVOICE_THRESHOLD;
  const [amendLicenseCount, { isLoading: isSubmitting }] =
    useAmendLicenseCountMutation();

  const eligiblePlans = orgPackages.filter(p =>
    ELIGIBLE_LICENSE_PLAN_NAMES.includes(p.package.name as PackageNameEnum)
  );

  const [counts, setCounts] = useState<Record<string, string>>({});
  const [msaChecked, setMsaChecked] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Maps plan ID → delta that was successfully added, shown as a success state in the UI
  const [succeededPlanDeltas, setSucceededPlanDeltas] = useState<
    Record<string, number>
  >({});

  // Seed empty entries for each plan once they load so getNewCount never returns undefined.
  useEffect(() => {
    if (eligiblePlans.length > 0 && Object.keys(counts).length === 0) {
      setCounts(Object.fromEntries(eligiblePlans.map(p => [p.id, ''])));
    }
    // eligiblePlans.length is intentional: we only want to seed counts once when plans first load,
    // not re-run whenever the array reference changes due to RTK Query re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligiblePlans.length]);

  const getNewCount = (orgPackageId: string) => counts[orgPackageId] ?? '';

  const setNewCount = (orgPackageId: string, value: string) =>
    setCounts(prev => ({ ...prev, [orgPackageId]: value }));

  const isValidNewCount = (orgPackageId: string) => {
    const val = parseInt(getNewCount(orgPackageId), 10);
    return !isNaN(val) && val > 0;
  };

  // Exclude plans with unknown current counts — we can't safely calculate the new total
  const getChangedPlans = () =>
    eligiblePlans.filter(
      p =>
        isValidNewCount(p.id) &&
        p.licenseCount !== null &&
        p.licenseCount !== undefined
    );

  const totalDelta = getChangedPlans().reduce((sum, p) => {
    return sum + parseInt(getNewCount(p.id), 10);
  }, 0);

  const isSubmitEnabled =
    msaChecked &&
    !hasPendingSubscriptionChange &&
    !hasTooManyPastDueInvoices &&
    getChangedPlans().length > 0 &&
    !isSubmitting &&
    !isRefetching;

  const handleSubmit = async () => {
    setSubmissionError(null);
    const changedPlans = getChangedPlans();
    const results = await Promise.allSettled(
      changedPlans.map(p =>
        amendLicenseCount({
          organizationId,
          productId: p.package.id,
          newLicenseCount:
            (p.licenseCount ?? 0) + parseInt(getNewCount(p.id), 10),
        }).unwrap()
      )
    );

    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    if (failed === 0) {
      showSuccessToast({
        id: 'add-licenses-success-toast',
        message: t('addLicensesModal.successToast'),
        stack: true,
      });
      setIsRefetching(true);
      await refetchOrganizationPackages();
      if (!isMountedRef.current) return;
      onClose();
      onSuccess?.();
    } else if (succeeded > 0) {
      // Partial success — keep modal open so the user can retry the failed plans.
      // Capture succeeded deltas and failed plan names before clearing counts.
      const newlySucceeded = changedPlans.filter(
        (_, i) => results[i].status === 'fulfilled'
      );
      const failedPlans = changedPlans.filter(
        (_, i) => results[i].status === 'rejected'
      );
      const failedPlanNames = failedPlans
        .map(p => `${p.package.displayName} (+${getNewCount(p.id)})`)
        .join(', ');

      setSucceededPlanDeltas(prev => ({
        ...prev,
        ...Object.fromEntries(
          newlySucceeded.map(p => [p.id, parseInt(getNewCount(p.id), 10)])
        ),
      }));
      setCounts(prev => ({
        ...prev,
        ...Object.fromEntries(newlySucceeded.map(p => [p.id, ''])),
      }));
      setSubmissionError(
        t('addLicensesModal.partialErrorToast', { plans: failedPlanNames })
      );
    } else {
      // All failed — show inline so it's hard to miss
      setSubmissionError(t('addLicensesModal.errorBanner'));
    }
  };

  return {
    eligiblePlans,
    isLoading:
      isLoadingPackages ||
      isLoadingInvoices ||
      isLoadingPendingSubscriptionChange,
    hasPendingSubscriptionChange,
    hasTooManyPastDueInvoices,
    getNewCount,
    setNewCount,
    isValidNewCount,
    msaChecked,
    setMsaChecked,
    totalDelta,
    getChangedPlans,
    isSubmitting,
    isSubmitEnabled,
    submissionError,
    succeededPlanDeltas,
    handleSubmit,
  };
};
